// frontend/src/components/AddLookModal.js
// Shared "Add Look" modal used by R3 (StaffBookings), R4, and R7 (FreelancerBookings).
//
// Features:
//  - Provider consent gate: checks /api/looks/provider-consent on first open.
//    If not agreed, shows LooksProviderConsentModal before allowing uploads.
//  - Multi-image: up to 4 files per look. Shows inline previews before upload.
//  - Add-to-existing mode: when `existingLook` prop is supplied (booking already
//    has a look with < 4 images), adds images to that look instead of creating a new one.
//
// Props:
//   booking         — completed booking object
//   existingLook    — { lookId, status, imageCount } or null (from eligible-bookings API)
//   getAuthHeaders  — fn returning { Authorization: 'Bearer ...' } or null
//   getApiBaseUrl   — fn returning base API URL string or null
//   onClose         — called when the modal should be dismissed
//   onSuccess       — called with the bookingId when done, so parent can refresh eligibility

import React, { useState, useEffect, useRef } from "react";
import { prepareImageForUpload } from "../utils/imageUploadPolicy";
import axios from "axios";
import Cropper from "react-easy-crop";
import LooksProviderConsentModal from "./LooksProviderConsentModal";
import ImageUploadField from "./ImageUploadField";

const MAX_IMAGES = 4;
// When a JPEG needs client-side orientation baking, cap the longest edge so the
// re-encoded upload stays well under the server's 8MB limit.
const MAX_IMAGE_EDGE = 2560;

// Reads the EXIF orientation tag (0x0112) from a JPEG APP1 "Exif" segment.
// Returns 1-8, or null when absent/unreadable (e.g. PNG/WebP or no EXIF).
function readJpegExifOrientation(arrayBuffer) {
  const view = new DataView(arrayBuffer);
  if (view.byteLength < 4 || view.getUint16(0, false) !== 0xffd8) return null;

  let offset = 2;
  while (offset + 4 <= view.byteLength) {
    if (view.getUint8(offset) !== 0xff) {
      offset += 1;
      continue;
    }
    const marker = view.getUint8(offset + 1);
    // Standalone markers (SOI, EOI, RSTn, TEM, ...) carry no length field.
    if (
      marker === 0xff ||
      (marker >= 0xd0 && marker <= 0xd7) ||
      marker === 0xd8 ||
      marker === 0xd9
    ) {
      offset += 2;
      continue;
    }
    const length = view.getUint16(offset + 2, false);
    if (length < 2) break;
    const segmentEnd = offset + 2 + length;
    // APP1 can be Exif — parse the TIFF IFD0 for tag 0x0112 (Orientation).
    if (
      marker === 0xe1 &&
      segmentEnd <= view.byteLength &&
      offset + 10 <= view.byteLength
    ) {
      let tag = "";
      for (let i = 0; i < 6; i += 1) {
        tag += String.fromCharCode(view.getUint8(offset + 4 + i));
      }
      if (tag === "Exif\0\0") {
        const orientation = parseExifOrientation(view, offset + 10);
        if (orientation) return orientation;
      }
    }
    offset = segmentEnd;
  }
  return null;
}

function parseExifOrientation(view, tiffStart) {
  if (tiffStart + 8 > view.byteLength) return null;
  const isLittle = view.getUint16(tiffStart, false) === 0x4949; // "II" = little-endian
  const ifd0 = tiffStart + view.getUint32(tiffStart + 4, isLittle);
  if (ifd0 + 2 > view.byteLength) return null;
  const entryCount = view.getUint16(ifd0, isLittle);
  for (let i = 0; i < entryCount; i += 1) {
    const entry = ifd0 + 2 + i * 12;
    if (entry + 12 > view.byteLength) break;
    if (view.getUint16(entry, isLittle) === 0x0112) {
      return view.getUint16(entry + 8, isLittle);
    }
  }
  return null;
}

function scaleToMaxDim(width, height, maxEdge) {
  const longest = Math.max(width, height);
  if (longest <= maxEdge) return { width, height };
  const scale = maxEdge / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

function canvasToJpegBlob(canvas) {
  return new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
}

function fileFromBlob(blob, original) {
  const name = original.name.replace(/\.(png|webp|jpe?g)$/i, ".jpg");
  return new File([blob], name, { type: "image/jpeg", lastModified: Date.now() });
}

// Crops an image (by object URL) to the pixel area reported by react-easy-crop
// and resolves with a JPEG blob. Used by the 4:3 drag-to-fit crop step.
function cropImageToBlob(imageSrc, pixels) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      try {
        // Output exactly the 4:5 frame region shown in the preview — true WYSIWYG.
        // If the photo is smaller than the frame (zoomed out to fit a taller or
        // wider photo), the area of the frame beyond the photo is preserved as
        // black/empty space, exactly matching what the professional sees framed.
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(pixels.width));
        canvas.height = Math.max(1, Math.round(pixels.height));
        const ctx = canvas.getContext("2d");
        ctx.drawImage(
          image,
          pixels.x,
          pixels.y,
          pixels.width,
          pixels.height,
          0,
          0,
          canvas.width,
          canvas.height,
        );
        canvas.toBlob(resolve, "image/jpeg", 0.92);
      } catch (err) {
        reject(err);
      }
    };
    image.onerror = reject;
    image.src = imageSrc;
  });
}

// Crops to exactly the 4:5 frame shown in the preview (true WYSIWYG). Instead
// of trusting react-easy-crop's croppedAreaPixels (which can be inaccurate when
// the photo doesn't cover the frame, e.g. zoomed out for a tall/wide photo), we
// read the rendered frame + media rects from the DOM and draw the media into a
// frame-sized canvas at its on-screen position. Frame area the photo doesn't
// cover stays near-black, matching the preview.
function cropToFrame(imageSrc, frameRect, mediaRect) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      try {
        const naturalW = image.naturalWidth || 0;
        const naturalH = image.naturalHeight || 0;
        const mw = mediaRect.width;
        const mh = mediaRect.height;
        if (!naturalW || !naturalH || mw <= 0 || mh <= 0) {
          resolve(null);
          return;
        }
        const sx = naturalW / mw;
        const sy = naturalH / mh;
        const outW = Math.max(1, Math.round(frameRect.width * sx));
        const outH = Math.max(1, Math.round(frameRect.height * sy));
        const imgX = Math.round((mediaRect.left - frameRect.left) * sx);
        const imgY = Math.round((mediaRect.top - frameRect.top) * sy);
        const imgW = Math.round(mw * sx);
        const imgH = Math.round(mh * sy);
        const canvas = document.createElement("canvas");
        canvas.width = outW;
        canvas.height = outH;
        const ctx = canvas.getContext("2d");
        // Fill the frame background (dark) where the photo doesn't cover it.
        ctx.fillStyle = "#0f172a";
        ctx.fillRect(0, 0, outW, outH);
        // Draw the photo at its on-screen position; canvas clips outside frame.
        ctx.drawImage(image, imgX, imgY, imgW, imgH);
        canvas.toBlob(resolve, "image/jpeg", 0.92);
      } catch (err) {
        reject(err);
      }
    };
    image.onerror = reject;
    image.src = imageSrc;
  });
}

// Bakes EXIF orientation into the pixels for JPEGs that need rotation, so the
// uploaded file matches the preview the user saw. On-the-fly camera captures
// (portrait phone shots) often rely on EXIF orientation; baking client-side
// guarantees correctness even if the server-side EXIF handling is unavailable.
// Returns the original file untouched when no rotation is needed or baking
// cannot be performed (the server's EXIF-aware watermark pipeline still covers
// that case).
async function normalizeImageOrientation(file) {
  if (file.type !== "image/jpeg" && file.type !== "image/jpg") {
    return file; // PNG/WebP carry no EXIF orientation in practice
  }

  let orientation = null;
  try {
    orientation = readJpegExifOrientation(await file.arrayBuffer());
  } catch (err) {
    return file;
  }

  if (!orientation || orientation === 1) {
    return file; // already correct — keep the original to avoid quality loss
  }

  try {
    // createImageBitmap applies EXIF orientation by default ("from-image"),
    // giving us a correctly-oriented bitmap with proper width/height.
    const bitmap = await createImageBitmap(file, {
      imageOrientation: "from-image",
    });
    try {
      const { width, height } = scaleToMaxDim(
        bitmap.width,
        bitmap.height,
        MAX_IMAGE_EDGE,
      );
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(bitmap, 0, 0, width, height);
      const blob = await canvasToJpegBlob(canvas);
      return blob ? fileFromBlob(blob, file) : file;
    } finally {
      if (typeof bitmap.close === "function") bitmap.close();
    }
  } catch (err) {
    return file; // if baking fails, keep the original (server .rotate() still fixes EXIF)
  }
}

function AddLookModal({
  booking,
  existingLook = null,
  getAuthHeaders,
  getApiBaseUrl,
  onClose,
  onSuccess,
  onAddAnother,
}) {
  const [consentStatus, setConsentStatus] = useState("loading"); // "loading" | "agreed" | "needed"
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [mainFileIndex, setMainFileIndex] = useState(0); // create-mode "Main" pick
  const [settingMain, setSettingMain] = useState(false); // existing-look main update
  const [submitting, setSubmitting] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const replaceInputRef = useRef(null);
  const cropContainerRef = useRef(null);

  // 4:3 drag-to-fit crop step (opens after the professional selects images)
  const [cropping, setCropping] = useState(false);
  const [cropIndex, setCropIndex] = useState(0);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  // zoom starts at 1 ("cover") so the photo always fills the 4:5 frame — the
  // preview is WYSIWYG (what you see is what publishes, no black bars).
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // Add-to-existing mode: current images on the look, plus replace/remove state
  const [existingImages, setExistingImages] = useState([]);
  const [existingCount, setExistingCount] = useState(
    existingLook?.imageCount || 0,
  );
  const [removing, setRemoving] = useState(false);
  const [replaceTargetIndex, setReplaceTargetIndex] = useState(null);

  const isAddMode = Boolean(existingLook?.lookId);
  const alreadyUsed = existingCount;
  const remaining = MAX_IMAGES - alreadyUsed;

  const serviceName =
    booking?.serviceId?.name || booking?.serviceName || "Service";
  const customerName =
    (booking?.customerId
      ? `${booking.customerId.firstName || ""} ${booking.customerId.lastName || ""}`.trim()
      : null) ||
    booking?.customerName ||
    booking?.clientName ||
    "Customer";
  const dateLabel =
    booking?.appointmentDate || booking?.bookingDate
      ? new Date(
          (booking.appointmentDate || booking.bookingDate) + "T12:00:00",
        ).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        })
      : "";

  // Check provider consent on mount
  useEffect(() => {
    const checkConsent = async () => {
      try {
        const headers = getAuthHeaders();
        const baseURL = getApiBaseUrl();
        if (!headers || !baseURL) {
          setConsentStatus("agreed");
          return;
        }
        const res = await axios.get(`${baseURL}/api/looks/provider-consent`, {
          headers,
        });
        setConsentStatus(res.data?.agreed ? "agreed" : "needed");
      } catch {
        setConsentStatus("agreed"); // Non-fatal — server will enforce on upload
      }
    };
    checkConsent();
  }, [getAuthHeaders, getApiBaseUrl]);

  // Build object URL previews when files change
  useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [files]);

  // Fetch the current images when adding to an existing look so the
  // professional can see, remove, or replace them before adding more.
  useEffect(() => {
    if (!isAddMode || !existingLook?.lookId) return;
    let cancelled = false;
    (async () => {
      try {
        const headers = getAuthHeaders();
        const baseURL = getApiBaseUrl();
        if (!headers || !baseURL) return;
        const res = await axios.get(
          `${baseURL}/api/looks/${existingLook.lookId}`,
          { headers },
        );
        const imgs = Array.isArray(res.data?.look?.images)
          ? res.data.look.images
          : [];
        if (cancelled) return;
        setExistingImages(imgs);
        if (imgs.length > 0) setExistingCount(imgs.length);
      } catch {
        // Non-fatal — fall back to the imageCount reported by the booking.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAddMode, existingLook?.lookId, getAuthHeaders, getApiBaseUrl]);

  if (!booking) return null;

  if (consentStatus === "loading") {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "12px",
            padding: "28px",
            fontSize: "14px",
            color: "#6b7280",
          }}
        >
          Loading…
        </div>
      </div>
    );
  }

  if (consentStatus === "needed") {
    return (
      <LooksProviderConsentModal
        getAuthHeaders={getAuthHeaders}
        getApiBaseUrl={getApiBaseUrl}
        onAgreed={() => setConsentStatus("agreed")}
        onDismiss={onClose}
      />
    );
  }

  const handleSelectedFiles = async (selection) => {
    const selected = (Array.isArray(selection) ? selection : [selection]).filter(Boolean);
    const allowed = selected.slice(0, remaining);
    if (allowed.length === 0) return;

    setProcessing(true);
    setError("");
    try {
      const normalized = [];
      for (const file of allowed) {
        try {
          normalized.push(await normalizeImageOrientation(file));
        } catch (err) {
          normalized.push(file); // never drop a photo because normalization failed
        }
      }
      setFiles(normalized);
      setMainFileIndex(0);
      // Open the 4:3 crop wizard for the first selected image.
      setCropIndex(0);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setCropping(true);
    } finally {
      setProcessing(false);
    }
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Remove an already-uploaded image from the look (DELETE endpoint).
  const removeExistingImage = async (index) => {
    if (!isAddMode || replaceTargetIndex !== null) return;
    setRemoving(true);
    setError("");
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;
      const res = await axios.delete(
        `${baseURL}/api/looks/${existingLook.lookId}/images/${index}`,
        { headers },
      );
      setExistingImages((prev) => prev.filter((_, i) => i !== index));
      setExistingCount((c) => Math.max(0, c - 1));
      setSuccess(res.data?.message || "Image removed.");
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Unable to remove image. Please try again.",
      );
    } finally {
      setRemoving(false);
    }
  };

  // Set an existing photo as the look's main/cover (PATCH endpoint).
  const makeExistingMain = async (index) => {
    if (!isAddMode || removing || settingMain || replaceTargetIndex !== null) return;
    setSettingMain(true);
    setError("");
    setSuccess("");
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;
      const res = await axios.patch(
        `${baseURL}/api/looks/${existingLook.lookId}/main-image`,
        { index },
        { headers },
      );
      setExistingImages((prev) => {
        if (index <= 0 || index >= prev.length) return prev;
        const next = [...prev];
        const [chosen] = next.splice(index, 1);
        next.unshift(chosen);
        return next;
      });
      setSuccess(res.data?.message || "Main image updated.");
    } catch (err) {
      setError(
        err?.response?.data?.message || "Unable to update main image.",
      );
    } finally {
      setSettingMain(false);
    }
  };

  // Replace flow: pick a target existing image, choose a new photo (goes
  // through the same 4:5 crop wizard), then swap on submit.
  const startReplace = (index) => {
    if (removing) return;
    setReplaceTargetIndex(index);
    setError("");
    setSuccess("");
    setTimeout(() => {
      if (replaceInputRef.current) replaceInputRef.current.click();
    }, 0);
  };

  const handleReplaceFileChange = async (e) => {
    const selected = e.target.files?.[0];
    if (!selected || replaceTargetIndex === null) return;
    setProcessing(true);
    setError("");
    try {
      let normalized = selected;
      try {
        normalized = await normalizeImageOrientation(selected);
      } catch (err) {
        // keep the original if normalization fails
      }
      setFiles([normalized]);
      setMainFileIndex(0);
      setCropIndex(0);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setCropping(true);
    } finally {
      setProcessing(false);
      if (replaceInputRef.current) replaceInputRef.current.value = "";
    }
  };

  const cancelReplace = () => {
    setReplaceTargetIndex(null);
    setFiles([]);
    setError("");
    setSuccess("");
  };

  const advanceCrop = () => {
    if (cropIndex + 1 >= files.length) {
      setCropping(false);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    } else {
      setCropIndex((i) => i + 1);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
    }
  };

  const applyCrop = async () => {
    const idx = cropIndex;
    const file = files[idx];
    const src = URL.createObjectURL(file);
    try {
      // Read the rendered frame + media geometry from the DOM so the output is
      // exactly the 4:5 frame the professional sees (accurate at any zoom).
      const frameEl = cropContainerRef.current
        ? cropContainerRef.current.querySelector('[data-testid="cropper"]')
        : null;
      const mediaEl = cropContainerRef.current
        ? cropContainerRef.current.querySelector("img.reactEasyCrop_Image")
        : null;
      if (frameEl && mediaEl) {
        const blob = await cropToFrame(
          src,
          frameEl.getBoundingClientRect(),
          mediaEl.getBoundingClientRect(),
        );
        if (blob) {
          const newFile = fileFromBlob(blob, file);
          setFiles((prev) => prev.map((f, i) => (i === idx ? newFile : f)));
        }
      } else if (croppedAreaPixels) {
        // Fallback if the rendered elements aren't available yet.
        const blob = await cropImageToBlob(src, croppedAreaPixels);
        if (blob) {
          const newFile = fileFromBlob(blob, file);
          setFiles((prev) => prev.map((f, i) => (i === idx ? newFile : f)));
        }
      }
    } catch (err) {
      // If cropping fails, keep the original image.
    } finally {
      URL.revokeObjectURL(src);
    }
    advanceCrop();
  };

  // "Skip" keeps the photo as-is (already orientation-normalized) — no forced
  // 4:5 crop, so skipping never silently cuts a photo that doesn't fit the
  // 4:5 frame (the gallery displays images at natural aspect).
  const skipCrop = () => {
    advanceCrop();
  };

  const skipAllCropping = () => {
    setCropping(false);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  };

  const handleSubmit = async () => {
    if (files.length === 0) {
      setError("Please select at least one image.");
      return;
    }
    setSubmitting(true);
    setError("");
    setSuccess("");

    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      // 1. Upload all selected images (server applies watermark). In create
      // mode the photo the professional marked "Main" is uploaded first so it
      // becomes index 0 (the main/cover the whole app uses).
      const uploadList =
        !isAddMode && mainFileIndex > 0 && mainFileIndex < files.length
          ? [
              files[mainFileIndex],
              ...files.slice(0, mainFileIndex),
              ...files.slice(mainFileIndex + 1),
            ]
          : files;
      const uploadData = new FormData();
      for (const f of uploadList) {
        uploadData.append("images", await prepareImageForUpload(f));
      }
      const uploadRes = await axios.post(
        `${baseURL}/api/looks/upload`,
        uploadData,
        { headers },
      );
      const uploadedImages = uploadRes.data?.images || [];
      if (uploadedImages.length === 0) {
        setError("Unable to upload images. Please try again.");
        return;
      }

      if (replaceTargetIndex !== null && isAddMode) {
        // Replace: remove the old image (server splices by index), then append
        // the new one. Upload happens first so nothing is lost on failure.
        await axios.delete(
          `${baseURL}/api/looks/${existingLook.lookId}/images/${replaceTargetIndex}`,
          { headers },
        );
        await axios.post(
          `${baseURL}/api/looks/${existingLook.lookId}/images`,
          { imageUrls: uploadedImages },
          { headers },
        );
        setSuccess("Image replaced successfully.");
        if (onSuccess) onSuccess(booking._id);
        return;
      }

      if (isAddMode) {
        // 2a. Add images to existing look
        await axios.post(
          `${baseURL}/api/looks/${existingLook.lookId}/images`,
          { imageUrls: uploadedImages },
          { headers },
        );
        setSuccess(`${uploadedImages.length} image(s) added to your Look.`);
      } else {
        // 2b. Create new draft look
        let createdLookId = null;

        // Quiet pre-check: if this booking already has a look, append images there.
        // This avoids an expected 409 conflict response in normal reuse flows.
        let conflictLookId = null;
        try {
          const mineRes = await axios.get(`${baseURL}/api/looks/mine`, {
            headers,
          });
          const mineLooks = Array.isArray(mineRes.data?.looks)
            ? mineRes.data.looks
            : [];
          const existingForBooking = mineLooks.find(
            (l) => String(l?.bookingId || "") === String(booking?._id || ""),
          );
          conflictLookId = existingForBooking?._id || null;
        } catch {
          // Non-fatal: fallback draft create below still handles conflicts safely.
        }

        if (conflictLookId) {
          await axios.post(
            `${baseURL}/api/looks/${conflictLookId}/images`,
            { imageUrls: uploadedImages },
            { headers },
          );
          setSuccess(
            `${uploadedImages.length} image(s) added to your existing Look.`,
          );
          if (onSuccess) onSuccess(booking._id);
          return;
        }

        // Use validateStatus so 409 is not thrown as an error — handle it inline.
        const draftRes = await axios.post(
          `${baseURL}/api/looks/drafts`,
          { bookingId: booking._id, imageUrls: uploadedImages },
          { headers, validateStatus: (s) => s < 500 },
        );
        if (draftRes.status === 409) {
          const draftConflictLookId = draftRes.data?.lookId;
          if (draftConflictLookId) {
            await axios.post(
              `${baseURL}/api/looks/${draftConflictLookId}/images`,
              { imageUrls: uploadedImages },
              { headers },
            );
            setSuccess(
              `${uploadedImages.length} image(s) added to your existing Look.`,
            );
            if (onSuccess) onSuccess(booking._id);
            return;
          }
          setError(
            draftRes.data?.message || "A look already exists for this booking.",
          );
          return;
        }
        if (draftRes.status >= 400) {
          throw new Error(
            draftRes.data?.message || "Failed to create look draft.",
          );
        }
        createdLookId = draftRes.data?.lookId;

        // Consent is automatically requested by backend, no need to call /request-consent
        if (createdLookId) {
          setSuccess("Look created and consent requested from your client.");
        }
      }

      if (onSuccess) onSuccess(booking._id);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Unable to save look. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "20px",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "white",
          borderRadius: "16px",
          padding: "28px",
          maxWidth: "520px",
          width: "100%",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: "0 0 4px 0", color: "#111827", fontSize: "18px" }}>
          {isAddMode
            ? remaining <= 0
              ? "📸 Manage Look Images"
              : "📸 Add More Images"
            : "📸 Add Look"}
        </h3>
        <p style={{ margin: "0 0 20px 0", fontSize: "13px", color: "#6b7280" }}>
          {serviceName} — {customerName}
          {dateLabel ? ` — ${dateLabel}` : ""}
        </p>

        {isAddMode && (
          <div
            style={{
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              borderRadius: "8px",
              padding: "10px 12px",
              fontSize: "13px",
              color: "#1d4ed8",
              marginBottom: "16px",
            }}
          >
            {remaining <= 0
              ? `This look already has ${MAX_IMAGES} images — the maximum allowed. You can replace or remove images below.`
              : `This booking already has a Look with ${alreadyUsed} image${alreadyUsed !== 1 ? "s" : ""}. You can add up to ${remaining} more.`}
          </div>
        )}

        {isAddMode && existingImages.length > 0 && (
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                fontSize: "13px",
                fontWeight: "600",
                color: "#374151",
                display: "block",
                marginBottom: "6px",
              }}
            >
              Current Images
            </label>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: "8px",
              }}
            >
              {existingImages.map((img, i) => (
                <div key={img.url || img._id || i} style={{ position: "relative" }}>
                  <img
                    src={img.url}
                    alt={`Current ${i + 1}`}
                    style={{
                      width: "100%",
                      aspectRatio: "1/1",
                      objectFit: "cover",
                      borderRadius: "8px",
                      border: "1px solid #e5e7eb",
                      display: "block",
                    }}
                  />
                  {i === 0 ? (
                    <span
                      title="Main image"
                      style={{
                        position: "absolute",
                        top: "3px",
                        left: "3px",
                        background: "rgba(202,138,4,0.95)",
                        color: "#fff",
                        fontSize: "10px",
                        fontWeight: "700",
                        padding: "2px 6px",
                        borderRadius: "999px",
                      }}
                    >
                      ★ Main
                    </span>
                  ) : (
                    <button
                      onClick={() => makeExistingMain(i)}
                      disabled={removing || settingMain || replaceTargetIndex !== null}
                      title="Make this the main image"
                      aria-label={`Make image ${i + 1} the main image`}
                      style={{
                        position: "absolute",
                        top: "3px",
                        left: "3px",
                        width: "20px",
                        height: "20px",
                        borderRadius: "999px",
                        background: "rgba(15,23,42,0.8)",
                        color: "#fbbf24",
                        border: "none",
                        fontSize: "11px",
                        lineHeight: "1",
                        cursor: removing || settingMain ? "wait" : "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity:
                          removing || settingMain || replaceTargetIndex !== null
                            ? 0.5
                            : 1,
                      }}
                    >
                      ★
                    </button>
                  )}
                  <button
                    onClick={() => removeExistingImage(i)}
                    disabled={removing || replaceTargetIndex !== null}
                    title="Remove image"
                    aria-label={`Remove image ${i + 1}`}
                    style={{
                      position: "absolute",
                      top: "3px",
                      right: "3px",
                      width: "20px",
                      height: "20px",
                      borderRadius: "999px",
                      background: "rgba(0,0,0,0.6)",
                      color: "#fff",
                      border: "none",
                      fontSize: "12px",
                      cursor: removing ? "wait" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      opacity: replaceTargetIndex !== null ? 0.4 : 1,
                    }}
                  >
                    ×
                  </button>
                  <button
                    onClick={() => startReplace(i)}
                    disabled={removing || (replaceTargetIndex !== null && files.length > 0)}
                    title="Replace image"
                    aria-label={`Replace image ${i + 1}`}
                    style={{
                      position: "absolute",
                      left: "3px",
                      bottom: "3px",
                      padding: "3px 6px",
                      borderRadius: "999px",
                      background: "rgba(15,23,42,0.75)",
                      color: "#fff",
                      border: "none",
                      fontSize: "10px",
                      fontWeight: "600",
                      cursor: removing ? "wait" : "pointer",
                      opacity:
                        replaceTargetIndex !== null && files.length > 0 ? 0.4 : 1,
                    }}
                  >
                    Replace
                  </button>
                </div>
              ))}
            </div>
            {replaceTargetIndex !== null && (
              <p
                style={{
                  fontSize: "12px",
                  color: "#1d4ed8",
                  margin: "8px 0 0 0",
                }}
              >
                {files.length > 0
                  ? "Replacement photo selected — press \"Replace Image\" to save."
                  : `Select a new photo for image #${replaceTargetIndex + 1}.`}{" "}
                <button
                  onClick={cancelReplace}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#dc2626",
                    fontSize: "12px",
                    fontWeight: "600",
                    cursor: "pointer",
                    padding: 0,
                    textDecoration: "underline",
                  }}
                >
                  Cancel
                </button>
              </p>
            )}
          </div>
        )}

        <ImageUploadField
          multiple
          variant="inline"
          label={
            replaceTargetIndex !== null
              ? `New Photo (replacing image #${replaceTargetIndex + 1})`
              : isAddMode && remaining <= 0
                ? "Look is full (4 images) — use Replace or Remove below"
                : `Upload Images${remaining > 1 ? ` (up to ${remaining})` : ""}`
          }
          buttonLabel="Choose photos"
          uploadOnSelect={false}
          showPreviews={false}
          max={Math.max(1, remaining)}
          disabled={
            processing ||
            replaceTargetIndex !== null ||
            (isAddMode && remaining <= 0)
          }
          onSelectFiles={handleSelectedFiles}
          onError={(message) => setError(message)}
          style={{ marginBottom: "12px" }}
        />
        {/* Hidden picker used by the per-image "Replace" buttons */}
        <input
          ref={replaceInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          style={{ display: "none" }}
          disabled={processing}
          onChange={handleReplaceFileChange}
        />

        {processing && (
          <p
            style={{
              fontSize: "12px",
              color: "#6b7280",
              margin: "0 0 12px 0",
            }}
          >
            Processing images…
          </p>
        )}

        {previews.length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: "8px",
              marginBottom: "14px",
            }}
          >
            {previews.map((url, i) => (
              <div key={i} style={{ position: "relative" }}>
                <img
                  src={url}
                  alt={`Preview ${i + 1}`}
                  style={{
                    width: "100%",
                    aspectRatio: "1/1",
                    objectFit: "cover",
                    borderRadius: "8px",
                    border: "1px solid #e5e7eb",
                  }}
                />
                {!isAddMode && (
                  <button
                    onClick={() => setMainFileIndex(i)}
                    title={
                      i === mainFileIndex
                        ? "Main image"
                        : "Make this the main image"
                    }
                    aria-label={
                      i === mainFileIndex
                        ? `Photo ${i + 1} is the main image`
                        : `Make photo ${i + 1} the main image`
                    }
                    style={{
                      position: "absolute",
                      top: "3px",
                      left: "3px",
                      padding: i === mainFileIndex ? "2px 7px" : "0",
                      width: i === mainFileIndex ? "auto" : "22px",
                      height: i === mainFileIndex ? "auto" : "22px",
                      borderRadius: "999px",
                      background:
                        i === mainFileIndex
                          ? "rgba(202,138,4,0.95)"
                          : "rgba(15,23,42,0.65)",
                      color: i === mainFileIndex ? "#fff" : "#e5e7eb",
                      border: "none",
                      fontSize: "11px",
                      fontWeight: "700",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {i === mainFileIndex ? "★ Main" : "★"}
                  </button>
                )}
                <button
                  onClick={() => removeFile(i)}
                  style={{
                    position: "absolute",
                    top: "3px",
                    right: "3px",
                    width: "20px",
                    height: "20px",
                    borderRadius: "999px",
                    background: "rgba(0,0,0,0.6)",
                    color: "white",
                    border: "none",
                    fontSize: "12px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        {!success && (
          <div
            style={{ fontSize: "11px", color: "#9ca3af", margin: "0 0 12px 0" }}
          >
            {((!isAddMode && previews.length > 1) ||
              (isAddMode && existingImages.length > 1)) && (
              <p
                style={{
                  margin: "0 0 6px 0",
                  color: "#1d4ed8",
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  borderRadius: "8px",
                  padding: "6px 10px",
                  fontSize: "12px",
                }}
              >
                💡 Tip: tap the <span style={{ fontWeight: 700 }}>★</span> on a
                photo to make it this look&apos;s main/cover image (it appears
                first on your profile and in shares).
              </p>
            )}
            <p style={{ margin: 0 }}>
              Images will be watermarked with the OGESTYLE logo before
              publishing.
            </p>
          </div>
        )}

        {error && (
          <p
            style={{ fontSize: "13px", color: "#dc2626", margin: "0 0 12px 0" }}
          >
            {error}
          </p>
        )}
        {success && (
          <p
            style={{ fontSize: "13px", color: "#16a34a", margin: "0 0 12px 0" }}
          >
            {success}
          </p>
        )}

        <div
          style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}
        >
          {success && onAddAnother && (
            <button
              onClick={onAddAnother}
              style={{
                padding: "10px 20px",
                fontSize: "14px",
                background: "#15803d",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: "600",
              }}
            >
              ➕ Add Another Look
            </button>
          )}
          <button
            onClick={onClose}
            style={{
              padding: "10px 20px",
              fontSize: "14px",
              background: "#f3f4f6",
              color: "#374151",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              cursor: "pointer",
              fontWeight: "500",
            }}
          >
            {success ? "Close" : "Cancel"}
          </button>
          {!success && (
            <button
              onClick={handleSubmit}
              disabled={submitting || processing || files.length === 0}
              style={{
                padding: "10px 20px",
                fontSize: "14px",
                background:
                  submitting || processing || files.length === 0
                    ? "#d1d5db"
                    : "#15803d",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor:
                  submitting || files.length === 0 ? "not-allowed" : "pointer",
                fontWeight: "600",
              }}
            >
              {submitting
                ? "Saving…"
                : replaceTargetIndex !== null
                  ? "Replace Image"
                  : isAddMode
                    ? remaining <= 0
                      ? "Look is full"
                      : `Add ${files.length > 0 ? files.length : ""} Image${files.length !== 1 ? "s" : ""}`
                    : "Create Look Draft"}
            </button>
          )}
        </div>
      </div>

      {cropping && files.length > 0 && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(2,6,23,0.8)",
            zIndex: 1200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "480px",
              padding: "16px",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              maxHeight: "92vh",
              overflowY: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span
                style={{ fontWeight: 700, fontSize: "15px", color: "#111827" }}
              >
                Crop Look Photo {cropIndex + 1} of {files.length}
              </span>
              <button
                onClick={skipAllCropping}
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#2563eb",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Skip all
              </button>
            </div>
            <p style={{ margin: 0, fontSize: "12px", color: "#6b7280" }}>
              Drag to position and use the slider/pinch to zoom. The 4:5 frame is
              exactly what publishes — if the photo doesn't fill the frame, the
              rest stays black. Zoom out to bring the whole subject into view.
            </p>
            <div
              ref={cropContainerRef}
              style={{
                position: "relative",
                width: "100%",
                aspectRatio: "4/5",
                background: "#0f172a",
                borderRadius: "10px",
                overflow: "hidden",
              }}
            >
              {previews[cropIndex] ? (
                <Cropper
                  image={previews[cropIndex]}
                  crop={crop}
                  zoom={zoom}
                  minZoom={0.5}
                  maxZoom={4}
                  aspect={4 / 5}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={(_, pixels) =>
                    setCroppedAreaPixels(pixels)
                  }
                />
              ) : null}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  whiteSpace: "nowrap",
                }}
              >
                Zoom
              </span>
              <input
                type="range"
                min={0.5}
                max={4}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                style={{ flex: 1 }}
              />
            </div>
            <div
              style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}
            >
              <button
                onClick={skipCrop}
                style={{
                  padding: "10px 18px",
                  fontSize: "14px",
                  background: "#f3f4f6",
                  color: "#374151",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: "500",
                }}
              >
                Skip
              </button>
              <button
                onClick={applyCrop}
                style={{
                  padding: "10px 18px",
                  fontSize: "14px",
                  background: "#1d4f80",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontWeight: "600",
                }}
              >
                {cropIndex + 1 >= files.length ? "Done" : "Next"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddLookModal;
